import React from "react";
import { graphql } from "gatsby";
import Img from "gatsby-image";
import { Layout } from "~/templates/Layout";
import { colors } from "~/utils/colors";
import { breakPoints, fontFamily, siteWidth } from "~/utils/variables";
import { renderHtmlAst } from "~/molecules/BaseBody";
import { InquiryForm } from "~/organisms/InquiryForm";
import { ContactApplicationForm } from "~/molecules/ContactApplicationForm";
import { PATH } from "~/constant/path";
import { Helmet } from "react-helmet";

type Props = {
  data: {
    inquiry: GatsbyTypes.ContentfulInquiry;
  };
};

const INQUIRY_CONTACT_ID = `1mQC1ikKSDTXr7qzf7LFT3`;

const Inquiry: React.FC<Props> = ({ data }) => {
  const { inquiry } = data;
  const isContactPage = inquiry.contentful_id === INQUIRY_CONTACT_ID;

  if (isContactPage) {
    // /inquiry/contactページの場合は特別なデザイン
    return (
      <>
        <style jsx>{`
          .wrapper {
            -webkit-font-smoothing: antialiased;
            background: ${colors.lightGray};
            font-family: ${fontFamily.insight};
            font-feature-settings: "palt";
            min-height: 80vh;
            padding: 0 0 50px;
            text-rendering: optimizeLegibility;
          }

          .wrapper :global(h1),
          .wrapper :global(h2),
          .wrapper :global(h3),
          .wrapper :global(h4),
          .wrapper :global(h5),
          .wrapper :global(h6) {
            font-weight: 700;
          }

          .component {
            box-sizing: border-box;
            margin: 0 auto;
            max-width: 1140px;
            padding: 0 24px;
          }

          .title {
            color: ${colors.defaultText};
            font-size: 28px;
            font-weight: 700;
            letter-spacing: 0.04em;
            line-height: 1.4;
            margin: 0 0 32px;
            text-align: center;
          }

          .body {
            display: flex;
            flex-direction: column;
            gap: 16px;
            margin: 0 auto;
            max-width: 100%;
            width: 100%;
          }

          .description {
            min-width: 0;
            width: 100%;
          }

          /* BaseBody の .paragraph margin を上書きし、段落同士の余白だけ確保する */
          .description :global(> div > p.paragraph) {
            color: ${colors.defaultText};
            font-size: 15px;
            line-height: 1.8;
            margin: 0 0 20px;
          }

          .description :global(> div > p.paragraph:last-child) {
            margin-bottom: 0;
          }

          .description :global(h2) {
            border-left: 4px solid ${colors.orange};
            color: ${colors.defaultText};
            font-size: 18px;
            line-height: 1.4;
            margin: 32px 0 16px;
            padding: 0 0 0 12px;
          }

          .description :global(h3) {
            border-bottom: 1px solid ${colors.gray3};
            color: ${colors.defaultText};
            font-size: 16px;
            line-height: 1.4;
            margin: 28px 0 12px;
            padding: 0 0 8px;
          }

          .description :global(h4) {
            color: ${colors.defaultText};
            font-size: 15px;
            font-weight: 700;
            line-height: 1.4;
            margin: 24px 0 8px;
          }

          .description :global(a) {
            color: ${colors.orange};
            text-decoration: underline;
          }

          .description :global(a:hover) {
            text-decoration: none;
          }

          .formBlock {
            width: 100%;
          }

          @media (${breakPoints.minPc}) {
            .body {
              align-items: start;
              display: grid;
              gap: 0 40px;
              grid-template-columns: minmax(0, 440px) 480px;
              justify-content: center;
              width: fit-content;
            }
          }

          @media (${breakPoints.sp}) {
            .wrapper {
              padding: 0 0 40px;
            }

            .component {
              padding: 0 20px;
            }

            .title {
              font-size: 22px;
              margin-bottom: 24px;
            }

            .description :global(> div > p.paragraph) {
              margin-bottom: 16px;
            }

            .body {
              gap: 32px;
            }
          }
        `}</style>
        <Helmet>
          <title>お問い合わせ｜Refcome</title>
        </Helmet>
        <Layout
          title="お問い合わせ"
          breadCrumbItems={[PATH.TOP, PATH.INQUIRY_CONTACT]}
          breadCrumbBgColor={colors.lightGray}
        >
          <div className="wrapper">
            <div className="component">
              <h1 className="title">{inquiry.title}</h1>
              <div className="body">
                <div className="description">{renderHtmlAst(inquiry.body?.childMarkdownRemark?.htmlAst)}</div>
                {inquiry.hubspotFormId && (
                  <div className="formBlock">
                    <ContactApplicationForm hubspotFormId={inquiry.hubspotFormId} />
                  </div>
                )}
              </div>
            </div>
          </div>
        </Layout>
      </>
    );
  }

  // それ以外の場合は従来のデザイン
  return (
    <>
      <style jsx>{`
        .wrapper {
          background: ${colors.bgGrayGradient};
          padding: 0 0 50px;
          margin: 100px 0 0;
        }
        .container {
          display: flex;
          flex-direction: column;
          margin: 0 auto;
          max-width: ${siteWidth.default};
          padding: 0 30px;
        }
        .title {
          text-align: center;
          margin: 0;
        }
        .content {
          margin: 30px 0 0;
        }
        .image {
          margin: 30px 0 0;
          width: 100%;
        }
        @media (${breakPoints.sp}) {
          .body {
            margin: 0;
          }
          .form {
            margin: 20px 0 0;
          }
          .title {
            font-size: 24px;
          }
        }
        @media (${breakPoints.minPc}) {
          .content {
            display: flex;
            justify-content: center;
          }
          .body {
            margin: 0 30px 0 0;
            width: calc(60% - 30px);
          }
          .form {
            width: 40%;
          }
          .title {
            font-size: 32px;
          }
        }
      `}</style>
      <Layout hideBreadCrumbs noindex>
        <div className="wrapper">
          <div className="container">
            <h2 className="title">{inquiry.title}</h2>
            <div className="content">
              <div className="body">
                {renderHtmlAst(inquiry.body?.childMarkdownRemark?.htmlAst)}
                {inquiry?.image && (
                  <div className="image">
                    <Img fluid={inquiry?.image?.fluid} />
                  </div>
                )}
              </div>
              <div className="form">
                <InquiryForm thanksPath={`/inquiry/${inquiry.contentful_id}/thanks`} />
              </div>
            </div>
          </div>
        </div>
      </Layout>
    </>
  );
};

export default Inquiry;

export const inquiryFormQuery = graphql`
  query Inquiry($contentfulId: String) {
    inquiry: contentfulInquiry(contentful_id: { eq: $contentfulId }) {
      contentful_id
      id
      title
      hubspotFormId
      thanks {
        contentful_id
      }
      body {
        id
        childMarkdownRemark {
          htmlAst
        }
      }
      image {
        id
        contentful_id
        fluid(maxWidth: 800) {
          base64
          aspectRatio
          src
          srcSet
          sizes
        }
      }
    }
  }
`;
